import { STT_SAMPLE_RATE } from './shared-survey-project-response-mode.consts';
import {
  ProjectResponseModeEvent,
  ResponseMode,
} from './shared-survey-project-response-mode.types';

/**
 * Resolve whether a `startListening` call should skip the local mic.
 * An explicit request wins; a bare call (visibility / aggressive-recovery
 * restart, mode toggle) inherits the current session mode, so a Mobile App
 * session never re-acquires the real mic on recovery.
 */
export function resolveSkipLocalMic(
  requested: boolean | undefined,
  sessionSkipLocalMic: boolean
): boolean {
  return requested ?? sessionSkipLocalMic;
}

export function buildResponseModeChangedEvent(
  prev: ResponseMode,
  next: ResponseMode,
  preChangeTranscript: string
): Extract<ProjectResponseModeEvent, { type: 'response_mode_changed' }> {
  return {
    type: 'response_mode_changed',
    prev,
    next,
    preChangeTranscript,
  };
}

export function buildListeningStateChangedEvent(
  isListening: boolean,
  finalTranscript: string
): Extract<ProjectResponseModeEvent, { type: 'listening_state_changed' }> {
  return {
    type: 'listening_state_changed',
    isListening,
    finalTranscript,
  };
}

export function buildTranscriptChangedEvent(
  transcript: string,
  isFinal: boolean
): Extract<ProjectResponseModeEvent, { type: 'transcript_changed' }> {
  return {
    type: 'transcript_changed',
    transcript,
    isFinal,
  };
}

export function shouldEmitTranscriptChange(
  previousTranscript: string,
  nextTranscript: string
): boolean {
  return previousTranscript !== nextTranscript;
}

export function buildSttAudioConstraints(
  preferredDeviceId?: string
): MediaTrackConstraints {
  const constraints: MediaTrackConstraints = {
    echoCancellation: true,
    noiseSuppression: true,
    channelCount: 1,
    autoGainControl: true,
  };

  if (preferredDeviceId) {
    return { ...constraints, deviceId: { exact: preferredDeviceId } };
  }

  return constraints;
}

export function createSttAudioSource(stream: MediaStream): {
  audioContext: AudioContext;
  source: MediaStreamAudioSourceNode;
} {
  let pinnedContext: AudioContext | null = null;
  try {
    pinnedContext = new AudioContext({ sampleRate: STT_SAMPLE_RATE });
    const source = pinnedContext.createMediaStreamSource(stream);
    return { audioContext: pinnedContext, source };
  } catch {
    if (pinnedContext) {
      void pinnedContext.close();
    }
    // Older Firefox throws NotSupportedError from createMediaStreamSource
    // when the context's sample rate differs from the mic stream's native
    // rate; retry once with a default-rate context so voice mode still works.
    const fallbackContext = new AudioContext();
    try {
      const source = fallbackContext.createMediaStreamSource(stream);
      return { audioContext: fallbackContext, source };
    } catch (error) {
      void fallbackContext.close();
      throw error;
    }
  }
}
